// <neroli-slideshow> — accessible, single-item slideshow.
// Autoplay pauses on hover, focus-within, tab hidden, and prefers-reduced-motion.
//
// Slide videos are deferred. The markup ships <source data-src> with no src
// and no autoplay attribute, because `autoplay` makes the browser fetch the
// file whatever `preload` says — a hero video then competes with the LCP
// image from the first byte. The poster (the slide's image, or the video's
// own first frame) paints immediately, and this script attaches the real
// source at the moment chosen in the section's "Load slide videos" setting.
// Only the slide currently showing ever gets its video loaded.
class NeroliSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length === 0) return;

    this.prevButton = this.querySelector('[data-slideshow-prev]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));

    this.index = 0;
    this.count = this.slides.length;
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.timer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.videoMode = this.dataset.videoLoading === 'immediate' ? 'immediate' : 'delayed';
    const parsedDelay = parseInt(this.dataset.videoDelay, 10);
    this.videoDelay = Number.isNaN(parsedDelay) ? 7000 : parsedDelay;
    this.videosReady = false;
    this.videoTimer = null;

    this.onPrev = () => { this.stop(); this.go(this.index - 1); };
    this.onNext = () => { this.stop(); this.go(this.index + 1); };
    this.prevButton && this.prevButton.addEventListener('click', this.onPrev);
    this.nextButton && this.nextButton.addEventListener('click', this.onNext);

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.stop(); this.go(i); });
    });

    // Keyboard support on the region.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.stop(); this.go(this.index - 1); }
      if (e.key === 'ArrowRight') { this.stop(); this.go(this.index + 1); }
    });

    // Pause conditions.
    if (this.count > 1 && this.autoplay && !this.reduceMotion) {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      document.addEventListener('visibilitychange', this.onVisibility = () => {
        document.hidden ? this.pause() : this.play();
      });
      this.play();
    }

    // Basic touch swipe.
    if (this.count > 1) this.bindSwipe();

    this.scheduleVideos();
    this.update();
  }

  disconnectedCallback() {
    this.pause();
    if (this.videoTimer) { clearTimeout(this.videoTimer); this.videoTimer = null; }
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* ---- Video loading ---- */

  // "Straight away" hydrates on connect. "After the page has loaded" waits for
  // the load event and then the configured delay, so the video never competes
  // with anything the page needed first. The wait starts from `load` rather
  // than from now, otherwise a slow page would spend its delay while still
  // downloading and the video would arrive in the middle of it anyway.
  scheduleVideos() {
    if (!this.querySelector('video[data-slideshow-video]')) return;

    if (this.videoMode === 'immediate') {
      this.videosReady = true;
      this.hydrateVideo(this.slides[this.index]);
      return;
    }

    const arm = () => {
      this.videoTimer = setTimeout(() => {
        this.videoTimer = null;
        this.videosReady = true;
        this.hydrateVideo(this.slides[this.index]);
      }, this.videoDelay);
    };

    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
  }

  // Loads one slide's video, once. Called when the delay elapses and again
  // every time a slide becomes active, so a visitor who advances early still
  // gets that slide's video as soon as videos are allowed to load at all.
  hydrateVideo(slide) {
    if (!this.videosReady || !slide) return;

    const video = slide.querySelector('video[data-slideshow-video]');
    if (!video || video.dataset.hydrated === 'true') return;

    const source = video.querySelector('source[data-src]');
    if (!source || !source.dataset.src) return;

    video.dataset.hydrated = 'true';
    source.src = source.dataset.src;
    video.load();

    // Muted inline playback is allowed without a gesture, but a browser can
    // still refuse (data saver, battery saver). The poster stays in that
    // case, which is a perfectly good slide.
    const started = video.play();
    if (started && typeof started.catch === 'function') started.catch(() => {});
  }

  go(i) {
    this.index = (i + this.count) % this.count;
    this.update();
  }

  update() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // Keep focusable children out of tab order when off-screen.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
      // Nothing is gained by decoding a video nobody can see.
      if (!active) {
        const hidden = slide.querySelector('video[data-slideshow-video]');
        if (hidden && hidden.dataset.hydrated === 'true' && !hidden.paused) hidden.pause();
      }
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const current = this.slides[this.index];
    this.hydrateVideo(current);
    const playing = current && current.querySelector('video[data-slideshow-video]');
    if (playing && playing.dataset.hydrated === 'true' && playing.paused) {
      const resumed = playing.play();
      if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
    }
  }

  play() {
    if (!this.autoplay || this.reduceMotion || this.count < 2 || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  pause() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  // Permanent stop after user interaction — no surprise auto-advance.
  stop() {
    this.autoplay = false;
    this.pause();
  }

  bindSwipe() {
    let startX = 0;
    let dragging = false;
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX; dragging = true;
    }, { passive: true });
    this.track.addEventListener('touchend', (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      this.stop();
      this.go(dx < 0 ? this.index + 1 : this.index - 1);
    }, { passive: true });
  }
}

customElements.define('neroli-slideshow', NeroliSlideshow);
